import { Delete, Edit } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import RemoveIcon from '@mui/icons-material/Remove';
import { Backdrop, Box, Button, LinearProgress, Modal, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { MyToDisableForm } from "../../components";
import { MyConfirmDialog } from "../../components/MyConfirmDialog";
import { MyRenderCell } from "../../components/MyRenderCell";
import { createEntityToDisable, IEntityToDisable } from "../../models/entityToDisable";
import { createGroup, IGroup } from "../../models/groups";
import { addGroup, disableGroup, getGroups, removeGroup } from "../../redux";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from '../../redux/store';
import { MyBoxModal } from "../../styled-components";
import { MyForm } from './components';

interface Props {
    idParent?: string;
    isToAddGroups?: boolean;
}

export const Groups = ({ isToAddGroups, idParent }: Props) => {
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);
    const openDialog = () => setDialogOpen(!dialogOpen);

    const [disableOpen, setDisableOpen] = useState(false);
    const openDisableDialog = () => setDisableOpen(!disableOpen);

    const [editing, setEditing] = useState(false);

    const [groupToEdit, setGroupToEdit] = useState(createGroup({}));
    const [entityToDisable, setEntityToDisable] = useState(createEntityToDisable({}));

    const [pageSize, setPageSize] = useState<number>(10);

    const dispatch = useAppDispatch();
    const { isLoading, groups = [] } = useAppSelector((state: RootState) => state.groups);

    const [confirmAddDialog, setConfirmAdd] = useState(false);
    const openConfirmAddDialog = () => setConfirmAdd(!confirmAddDialog);

    const [confirmRemoveDialog, setConfirmRemove] = useState(false);
    const openConfirmRemoveDialog = () => setConfirmRemove(!confirmRemoveDialog);

    useEffect(() => {
        dispatch(getGroups(idParent) as any);
    }, [dispatch, idParent])

    const onDisableDialog = (group: IGroup) => {
        setEntityToDisable(createEntityToDisable(group));
        setEditing(true);
        openDisableDialog();
    }

    const onEdit = (group: IGroup) => {
        setGroupToEdit(createGroup(group));
        setEditing(true);
        openDialog();
    }
    const onAdd = () => {
        setGroupToEdit(createGroup({}));
        setEditing(false);
        openDialog();
    }

    const onDisable = (values: IEntityToDisable) => {
        dispatch(disableGroup(values) as any);
    }

    const onAddGroup = (id: number) => {
        if (idParent)
            dispatch(addGroup(id, idParent) as any);
    }

    const onRemoveGroup = (id: number) => {
        if (idParent)
            dispatch(removeGroup(id, idParent) as any);
    }

    const onConfirmAdd = (group: IGroup) => {
        setGroupToEdit(createGroup(group));
        openConfirmAddDialog();
    }

    const onConfirmRemove = (group: IGroup) => {
        setGroupToEdit(createGroup(group));
        openConfirmRemoveDialog();
    }

    const Columns: GridColDef[] = [
        {
            field: "action",
            headerName: "Action",
            width: isToAddGroups ? 70 : 210,
            sortable: false,
            align: "right",
            renderCell: (params) => {
                const onClick = (e: React.MouseEvent<HTMLElement>) => {
                    onEdit(params.row as any);
                };
                const disableGroup = (e: React.MouseEvent<HTMLElement>) => {
                    onDisableDialog(params.row as any);
                };
                const addItems = (e: React.MouseEvent<HTMLElement>) => {
                    navigate("/groups/" + params.row.id);
                };
                const addGroup = (e: React.MouseEvent<HTMLElement>) => {
                    onConfirmAdd(params.row as any);
                };
                const removeGroup = (e: React.MouseEvent<HTMLElement>) => {
                    onConfirmRemove(params.row as any);
                };
                return (
                    <>
                        {!isToAddGroups && (<>
                            <Tooltip title="Edit" >
                                <Button onClick={onClick}><Edit /></Button>
                            </Tooltip>
                            <Tooltip title="Disable" >
                                <Button onClick={disableGroup}><Delete /></Button>
                            </Tooltip>
                            <Tooltip title="Add Materials" >
                                <Button onClick={addItems}><FormatListNumberedIcon /></Button>
                            </Tooltip>
                        </>)}
                        {isToAddGroups && (params.row.subActivityId ?
                            (<>
                                <Tooltip title="Remove Group" >
                                    <Button onClick={removeGroup}><RemoveIcon /></Button>
                                </Tooltip>
                            </>) :
                            (<>
                                <Tooltip title="Add Group" >
                                    <Button onClick={addGroup}><AddIcon /></Button>
                                </Tooltip>
                            </>))}
                    </>
                )
            }
        },
        {
            field: 'id',
            headerName: 'id',
            width: 150,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 250,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'activeUntilDate',
            headerName: 'Active Until Date',
            width: 250,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
    ]

    return (
        <div>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <Box sx={{ width: '40%' }}>
                    <LinearProgress />
                </Box>
            </Backdrop>
            {!isToAddGroups && (
                <>
                    <Typography variant="h4" component="h1">Groups</Typography>
                    <br />
                    <Button onClick={onAdd}>Add</Button>
                </>
            )}
            <div style={{ height: "calc(100vh - 250px)", width: '100%', flexGrow: 1 }}>
                <DataGrid
                    rows={groups}
                    columns={Columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 50, 100]}
                    columnVisibilityModel={{ id: false, }}
                //checkboxSelection
                />
            </div>
            <Modal open={dialogOpen} onClose={openDialog}>
                <MyBoxModal>
                    <MyForm onCancel={openDialog} group={groupToEdit} editing={editing} />
                </MyBoxModal>
            </Modal>
            <Modal open={disableOpen} onClose={openDisableDialog}>
                <MyBoxModal>
                    <MyToDisableForm onCancel={openDisableDialog} entity={entityToDisable} onDisable={onDisable} />
                </MyBoxModal>
            </Modal>
            <Modal open={confirmAddDialog} onClose={openConfirmAddDialog}>
                <MyBoxModal>
                    <MyConfirmDialog onCancel={openConfirmAddDialog} entity={groupToEdit} onOk={onAddGroup} />
                </MyBoxModal>
            </Modal>
            <Modal open={confirmRemoveDialog} onClose={openConfirmRemoveDialog}>
                <MyBoxModal>
                    <MyConfirmDialog onCancel={openConfirmRemoveDialog} entity={groupToEdit} onOk={onRemoveGroup} />
                </MyBoxModal>
            </Modal>
        </div >
    )
}
